import React, { useEffect, useState } from 'react'
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Avatar,
} from '@mui/material'
import {
  ArrowBack,
  Assignment,
  CheckCircle,
  Block,
  TuneOutlined,
  RemoveCircleOutline,
} from '@mui/icons-material'
import { Helmet } from 'react-helmet'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { api } from '../../../../services/Main/Api'

const CompanyPlanManager = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const [company, setCompany] = useState(null)
  const [plans, setPlans] = useState([])
  const [selectedPlanId, setSelectedPlanId] = useState('')
  const [allFeatures, setAllFeatures] = useState([])
  const [overrides, setOverrides] = useState([])
  const [condoFeatures, setCondoFeatures] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [overrideDialog, setOverrideDialog] = useState({ open: false, featureId: null, featureName: '', isEnabled: false })
  const [overrideReason, setOverrideReason] = useState('')

  useEffect(() => {
    loadAll()
  }, [id])

  const loadAll = async () => {
    setLoading(true)
    try {
      const [companyRes, plansRes, featuresRes, overridesRes, condoFeaturesRes] = await Promise.all([
        api.get(`/condo/${id}/`),
        api.get('/plan/plans/', { params: { is_active: true, page_size: 100 } }),
        api.get('/plan/features/', { params: { page_size: 200 } }),
        api.get('/plan/condo-overrides/', { params: { condo_id: id } }),
        api.get(`/plan/condo-features/${id}/`),
      ])
      setCompany(companyRes.data)
      setPlans(plansRes.data.results || [])
      setAllFeatures(featuresRes.data.results || [])
      setOverrides(overridesRes.data.results || overridesRes.data || [])
      setCondoFeatures(condoFeaturesRes.data.features || [])

      const currentPlan = plansRes.data.results?.find(p => p.name === companyRes.data.type_plan)
      if (currentPlan) {
        setSelectedPlanId(currentPlan.id)
      }
    } catch (err) {
      console.error(err)
      enqueueSnackbar('Erro ao carregar dados', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleAssignPlan = async () => {
    if (!selectedPlanId) return
    try {
      setSaving(true)
      await api.post('/plan/assign-plan/', { condo_id: parseInt(id), plan_id: selectedPlanId })
      enqueueSnackbar('Plano atribuído com sucesso', { variant: 'success' })
      await loadAll()
    } catch (err) {
      const errorMsg = err?.response?.data?.message || err?.response?.data?.error || 'Erro ao atribuir plano'
      enqueueSnackbar(errorMsg, { variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const getFeatureState = (feature) => {
    const override = overrides.find(o => o.feature === feature.id)
    const isInCondoFeatures = condoFeatures.includes(feature.slug)

    if (override) {
      return override.is_enabled
        ? { status: 'override_on', label: 'Ativo', color: 'success', override }
        : { status: 'override_off', label: 'Bloqueado', color: 'error', override }
    }

    if (isInCondoFeatures) {
      return { status: 'plan_enabled', label: 'Ativo', color: 'success', override: null }
    }

    return { status: 'disabled', label: 'Inativo', color: 'default', override: null }
  }

  const handleToggleOverride = (feature) => {
    const state = getFeatureState(feature)

    if (state.override) {
      handleDeleteOverride(state.override.id)
    } else {
      const isCurrentlyEnabled = state.status === 'plan_enabled'
      setOverrideDialog({
        open: true,
        featureId: feature.id,
        featureName: feature.name,
        isEnabled: !isCurrentlyEnabled,
      })
      setOverrideReason('')
    }
  }

  const handleCreateOverride = async () => {
    try {
      setSaving(true)
      await api.post('/plan/condo-overrides/', {
        condo: parseInt(id),
        feature: overrideDialog.featureId,
        is_enabled: overrideDialog.isEnabled,
        reason: overrideReason,
      })
      enqueueSnackbar('Override criado com sucesso', { variant: 'success' })
      setOverrideDialog({ open: false, featureId: null, featureName: '', isEnabled: false })
      await loadAll()
    } catch (err) {
      const errorMsg = err?.response?.data
      if (errorMsg && typeof errorMsg === 'object') {
        const firstError = Object.values(errorMsg).flat()[0]
        enqueueSnackbar(firstError || 'Erro ao criar override', { variant: 'error' })
      } else {
        enqueueSnackbar('Erro ao criar override', { variant: 'error' })
      }
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteOverride = async (overrideId) => {
    try {
      setSaving(true)
      await api.delete(`/plan/condo-overrides/${overrideId}/`)
      enqueueSnackbar('Override removido com sucesso', { variant: 'success' })
      await loadAll()
    } catch (err) {
      enqueueSnackbar('Erro ao remover override', { variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const buildFeatureRows = () => {
    const modules = allFeatures.filter(f => f.category === 'MODULE')
    const features = allFeatures.filter(f => f.category === 'FEATURE')
    const rows = []

    modules.forEach(mod => {
      rows.push({ ...mod, isModule: true, indent: false })
      features
        .filter(f => f.parent === mod.id)
        .forEach(child => {
          rows.push({ ...child, isModule: false, indent: true })
        })
    })

    features
      .filter(f => !f.parent)
      .forEach(feature => {
        rows.push({ ...feature, isModule: false, indent: false })
      })

    return rows
  }

  const renderStatusChip = (feature) => {
    const state = getFeatureState(feature)
    const hasOverride = !!state.override

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Chip
          label={state.label}
          size="small"
          color={state.color}
          variant={hasOverride ? 'filled' : 'outlined'}
        />
        {hasOverride && (
          <Tooltip title={`Override: ${state.override.reason || 'Sem motivo'}`}>
            <TuneOutlined fontSize="small" color={state.status === 'override_on' ? 'success' : 'error'} />
          </Tooltip>
        )}
      </Box>
    )
  }

  const featureRows = buildFeatureRows()

  return (
    <>
      <Helmet>
        <title>Gerenciar Plano - Admin</title>
      </Helmet>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={saving}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container maxWidth="lg">
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(`/admin/company/${id}`)}
          >
            Voltar
          </Button>
          <Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }}>
            Gerenciar Plano {company ? `- ${company.name}` : ''}
          </Typography>
        </Box>

        {loading ? (
          <Skeleton variant="rounded" height={600} animation="pulse" />
        ) : (
          <>
            <Card sx={{ mb: 3 }}>
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <Assignment />
                  </Avatar>
                }
                title="Atribuir Plano"
              />
              <Divider />
              <CardContent>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <TextField
                    select
                    label="Plano"
                    size="small"
                    value={selectedPlanId}
                    onChange={(e) => setSelectedPlanId(e.target.value)}
                    sx={{ minWidth: 300 }}
                  >
                    <MenuItem value="">Selecione um plano</MenuItem>
                    {plans.map((plan) => (
                      <MenuItem key={plan.id} value={plan.id}>
                        {plan.name} ({plan.features?.length || 0} features)
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    variant="contained"
                    onClick={handleAssignPlan}
                    disabled={!selectedPlanId || saving}
                  >
                    Atribuir Plano
                  </Button>
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardHeader title="Funcionalidades" />
              <Divider />
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Funcionalidade</TableCell>
                      <TableCell>Categoria</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Override</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {featureRows.map((feature) => {
                      const state = getFeatureState(feature)
                      const isEnabled = state.status === 'plan_enabled' || state.status === 'override_on'

                      return (
                        <TableRow
                          key={feature.id}
                          sx={{
                            bgcolor: feature.isModule ? 'action.hover' : 'inherit',
                          }}
                        >
                          <TableCell>
                            <Box sx={{ pl: feature.indent ? 4 : 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                              {feature.isModule ? (
                                <Typography fontWeight="bold">{feature.name}</Typography>
                              ) : (
                                <Typography>{feature.name}</Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={feature.category === 'MODULE' ? 'Módulo' : 'Feature'}
                              size="small"
                              color={feature.category === 'MODULE' ? 'primary' : 'secondary'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {renderStatusChip(feature)}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={state.override ? 'Remover override' : 'Criar override'}>
                              <Switch
                                size="small"
                                checked={isEnabled}
                                onChange={() => handleToggleOverride(feature)}
                                color={state.override ? 'warning' : 'primary'}
                              />
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </>
        )}

        <Dialog
          open={overrideDialog.open}
          onClose={() => setOverrideDialog({ open: false, featureId: null, featureName: '', isEnabled: false })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Criar Override - {overrideDialog.featureName}
          </DialogTitle>
          <Divider />
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {overrideDialog.isEnabled
                ? 'Esta funcionalidade será ATIVADA para esta empresa, independente do plano.'
                : 'Esta funcionalidade será DESATIVADA para esta empresa, independente do plano.'
              }
            </Typography>
            <TextField
              label="Motivo"
              fullWidth
              size="small"
              multiline
              rows={2}
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="Ex: Cliente solicitou ativação antecipada"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOverrideDialog({ open: false, featureId: null, featureName: '', isEnabled: false })}>
              Cancelar
            </Button>
            <Button onClick={handleCreateOverride} variant="contained" disabled={saving}>
              Confirmar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  )
}

export default CompanyPlanManager
